import React from "react";
import { message } from "antd";
import { ChatCompletionRequest, ToolCall } from "../../../model_gen/chat";
import RequestOverview from "../../Request/RequestOverview";
import { RequestsService } from "../../../services/RequestsService";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { CopyIcon, EditIcon } from "lucide-react";
import { toast } from "sonner";

interface DatasetRequestEditProps {
  request: ChatCompletionRequest;
  onSave: (request: ChatCompletionRequest) => void;
  onSelect?: () => void;
  isEligible?: boolean;
  datasetId?: string;
  apiKey: string;
}

const DatasetRequestEdit: React.FC<DatasetRequestEditProps> = ({
  request,
  onSave,
  onSelect,
  isEligible = false,
  apiKey,
}) => {
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [editedContent, setEditedContent] = React.useState<string | null>(null);
  const [initialContent, setInitialContent] = React.useState<string | null>(
    null
  );
  const [pendingRequest, setPendingRequest] =
    React.useState<ChatCompletionRequest | null>(null);
  const [editingMessageIndex, setEditingMessageIndex] = React.useState<
    number | null
  >(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [editedToolCalls, setEditedToolCalls] = React.useState<
    ToolCall[] | null
  >(null);
  const [showAddToolCall, setShowAddToolCall] = React.useState(false);
  const [newToolCall, setNewToolCall] = React.useState({
    name: "",
    arguments: "",
  });

  const messages = request?.params?.messages || [];
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const hasAssistantMessage = lastMessage?.role === "assistant";

  const handleModalOpen = () => {
    // Initialize with either existing edited content or original content
    const datasetResponse = request.metadata?._maitai_dataset_request_response;
    const content =
      datasetResponse !== undefined && datasetResponse !== null
        ? (datasetResponse as string)
        : (lastMessage.content as string);
    setEditedContent(content);
    setInitialContent(content);
    setPendingRequest(request);
    const datasetToolCalls =
      request.metadata?._maitai_dataset_request_response_tool_calls;
    setEditedToolCalls(
      datasetToolCalls
        ? (datasetToolCalls as ToolCall[])
        : lastMessage?.tool_calls || []
    );
    setIsModalVisible(true);
  };

  const handleMessageEdit = (index: number, content: string) => {
    setEditedContent(content);
    setPendingRequest(request);
  };

  const handleModelChange = (model: string) => {
    const newRequest = {
      ...request,
      params: {
        ...request.params,
        model,
      },
    };
    setPendingRequest(newRequest);
  };

  const handleSave = () => {
    if (!pendingRequest) {
      toast.error("No request to save");
      return;
    }

    if (isEligible && (editedContent !== null || editedToolCalls !== null)) {
      // This request hasn't been added to the dataset yet
      // Only update content if it was edited
      const label = {
        role: "assistant",
        content: editedContent || lastMessage.content,
        tool_calls: editedToolCalls || lastMessage?.tool_calls,
      };
      pendingRequest.metadata._maitai_annotation_label = JSON.stringify(label);
      onSave(pendingRequest);
      setEditedContent(null);
      setInitialContent(null);
      setPendingRequest(null);
      setEditedToolCalls(null);
      setIsModalVisible(false);

      if (isEligible && onSelect) {
        onSelect();
      }
      return;
    }

    // Update the request response via the API
    setIsLoading(true);

    const payload = {
      message_content: editedContent ?? lastMessage.content,
      tool_calls: editedToolCalls ?? lastMessage.tool_calls,
    };

    RequestsService.updateRequestResponse(
      pendingRequest.request_id,
      payload,
      setIsLoading,
      apiKey
    ).subscribe(
      () => {
        onSave(pendingRequest);
        setEditedContent(null);
        setInitialContent(null);
        setPendingRequest(null);
        setEditedToolCalls(null);
        setIsModalVisible(false);
        toast.success("Message updated successfully");
      },
      (error: any) => {
        console.error("Failed to update message:", error);
        toast.error("Failed to update message");
        setIsLoading(false);
      }
    );
  };

  const handleCancel = () => {
    setEditedContent(null);
    setInitialContent(null);
    setPendingRequest(null);
    setEditingMessageIndex(null);
    setEditedToolCalls(null);
    setShowAddToolCall(false);
    setIsModalVisible(false);
  };

  const handleDeleteToolCall = (toolCallIndex: number) => {
    if (!editedToolCalls) return;
    const updatedToolCalls = editedToolCalls.filter(
      (_, index) => index !== toolCallIndex
    );
    setEditedToolCalls(updatedToolCalls);
    setPendingRequest(request); // Ensure pendingRequest is set for save button
  };

  const hasChanges =
    editedContent !== initialContent ||
    JSON.stringify(editedToolCalls) !== JSON.stringify(lastMessage?.tool_calls);

  const handleToolCallChange = (
    toolCallIndex: number,
    field: string,
    value: string
  ) => {
    if (!editedToolCalls) return;

    const updatedToolCalls = [...editedToolCalls];
    if (field === "name") {
      updatedToolCalls[toolCallIndex].function.name = value;
    } else if (field === "arguments") {
      updatedToolCalls[toolCallIndex].function.arguments = value;
    }

    setEditedToolCalls(updatedToolCalls);
  };

  const handleAddToolCall = () => {
    setShowAddToolCall(true);
  };

  const confirmAddToolCall = () => {
    if (!editedToolCalls) return;

    const newToolCallObj = {
      id: `call_${Date.now()}`,
      type: "function",
      function: {
        name: newToolCall.name,
        arguments: newToolCall.arguments,
      },
    };

    setEditedToolCalls([...editedToolCalls, newToolCallObj]);
    setNewToolCall({ name: "", arguments: "" });
    setShowAddToolCall(false);
  };

  const getMessageContent = (message: any, index: number) => {
    // Check if this is the last message
    const isLastMessage = index === (request.params.messages?.length || 0) - 1;
    
    if (isLastMessage) {
      // Check for annotation label
      if (request.metadata?._maitai_annotation_label) {
        const annotationLabel =
          typeof request.metadata._maitai_annotation_label === "string"
            ? JSON.parse(request.metadata._maitai_annotation_label)
            : request.metadata._maitai_annotation_label;
        return annotationLabel.content;
      }
      
      // Check for test response
      const testResponse = request.metadata?._maitai_test_request_response as
        | string
        | undefined;
      if (testResponse !== undefined && testResponse !== null) {
        return testResponse;
      }
    }
    
    return message.content;
  };

  const getMessageToolCalls = (message: any, index: number) => {
    const isLastMessage = index === (request.params.messages?.length || 0) - 1;
    
    if (isLastMessage && message.role === "assistant") {
      // Check for dataset response tool calls
      if (request.metadata?._maitai_dataset_request_response_tool_calls) {
        return request.metadata._maitai_dataset_request_response_tool_calls;
      }
      
      // Check for annotation label
      if (request.metadata?._maitai_annotation_label) {
        const annotationLabel =
          typeof request.metadata._maitai_annotation_label === "string"
            ? JSON.parse(request.metadata._maitai_annotation_label)
            : request.metadata._maitai_annotation_label;
        return annotationLabel.tool_calls;
      }
    }
    
    return message.tool_calls;
  };

  const formatMessagesForCopy = () => {
    const messages = request?.params?.messages || [];
    return messages.map((message, index) => {
      const content = getMessageContent(message, index);
      let result = `${message.role.toUpperCase()}: ${content || ""}`;
      
      const toolCalls = getMessageToolCalls(message, index);
      if (toolCalls && toolCalls.length > 0) {
        const toolCallsFormatted = toolCalls.map((tool: ToolCall) => {
          return `[TOOL: ${tool.function.name}, ARGS: ${tool.function.arguments}]`;
        }).join(" ");
        
        result += ` ${toolCallsFormatted}`;
      }
      
      return result;
    }).join("\n");
  };

  const handleCopyToClipboard = () => {
    const formattedText = formatMessagesForCopy();
    navigator.clipboard.writeText(formattedText)
      .then(() => {
        toast.success("Messages copied to clipboard");
      })
      .catch(() => {
        toast.error("Failed to copy messages");
      });
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleModalOpen}
        disabled={!hasAssistantMessage}
        title={
          !hasAssistantMessage ? "No assistant message to edit" : "Edit request"
        }
        className="gap-1"
      >
        <EditIcon className="w-4 h-4" />
      </Button>

      <Dialog open={isModalVisible} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-background text-foreground border-border">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-gc-med text-foreground">
              Edit Request
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            <RequestOverview
              request={request}
              isEditable={true}
              onMessageEditComplete={handleMessageEdit}
              onModelChange={handleModelChange}
              editingMessageIndex={editingMessageIndex}
              onEditingMessageChange={setEditingMessageIndex}
              editMode="final-assistant"
              showDetails={false}
              expanded={true}
              onToolCallChange={handleToolCallChange}
              onAddToolCall={handleAddToolCall}
              onConfirmAddToolCall={confirmAddToolCall}
              onDeleteToolCall={handleDeleteToolCall}
              showAddToolCall={showAddToolCall}
              newToolCall={newToolCall}
              setNewToolCall={setNewToolCall}
              setShowAddToolCall={setShowAddToolCall}
            />
          </div>

          <DialogFooter className="flex-shrink-0 gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              variant="outline" 
              onClick={handleCopyToClipboard}
              className="gap-2"
            >
              <CopyIcon className="w-4 h-4" />
              Copy Messages
            </Button>
            <Button
              onClick={handleSave}
              disabled={!pendingRequest || (!hasChanges && !editedToolCalls)}
              className="gap-2"
            >
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DatasetRequestEdit;
